import { useState } from "react";
import { useNavigate, useSearchParams, useLocation, Link } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

const GoogleIcon = () => (
  <svg width="17" height="17" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/></svg>
);

export default function AuthPage() {
  const location = useLocation();
  const [params] = useSearchParams();
  const isReset = location.pathname === "/reset-password";
  const [mode, setMode] = useState(isReset ? "reset" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [devLink, setDevLink] = useState(null);
  const [showPw, setShowPw] = useState(false);
  const { onAuth } = useAuth();
  const nav = useNavigate();

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/my-learning";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const titles = { login: "SIGN IN", register: "CREATE ACCOUNT", forgot: "RESET PASSWORD", reset: "CHOOSE NEW PASSWORD" };
  const subtitles = { forgot: "Enter your email and we'll send a reset link", reset: "Pick a new password for your account" };
  const btnLabels = { login: "SIGN IN", register: "SIGN UP", forgot: "SEND RESET LINK", reset: "SET NEW PASSWORD" };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email, origin_url: window.location.origin });
        toast.success(data.message);
        setDevLink(data.dev_reset_link || null);
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { token: params.get("token") || "", password: form.password });
        toast.success("Password updated — sign in with your new password");
        nav("/auth");
        setMode("login");
      } else {
        const { data } = await api.post(`/auth/${mode}`, mode === "login"
          ? { email: form.email, password: form.password }
          : { ...form, origin_url: window.location.origin });
        onAuth(data);
        toast.success(mode === "login" ? "Welcome back!" : "Account created!");
        nav(params.get("next") || "/");
      }
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 pt-32 pb-16">
      <div className="forge-card p-8">
        <h1 className="font-display font-bold text-2xl text-center">{titles[mode]}</h1>
        <p className="text-center text-sm mt-1 mb-6" style={{ color: "var(--muted)" }}>
          {subtitles[mode] || "to Unreal Academy — Master Unreal Engine"}
        </p>
        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input data-testid="auth-name-input" required placeholder="Full name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" />
          )}
          {mode !== "reset" && (
            <input data-testid="auth-email-input" required type="email" placeholder="Email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark" />
          )}
          {mode !== "forgot" && (
            <div className="relative">
              <input data-testid="auth-password-input" required type={showPw ? "text" : "password"}
                placeholder={mode === "reset" ? "New password (min 6 chars)" : "Password (min 6 chars)"} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-dark pr-11" />
              <button type="button" data-testid="password-visibility-toggle" aria-label={showPw ? "Hide password" : "Show password"}
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:opacity-80" style={{ color: "var(--muted)" }}>
                {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>
          )}
          <button data-testid="auth-submit-btn" disabled={busy} className="btn-primary w-full btn-block">
            {busy ? "…" : btnLabels[mode]}
          </button>
        </form>
        {(mode === "login" || mode === "register") && (
          <>
            <div className="flex items-center gap-3 my-4">
              <div className="h-px flex-1" style={{ background: "var(--line)" }} />
              <span className="text-xs font-display" style={{ color: "var(--muted)", letterSpacing: 2 }}>OR</span>
              <div className="h-px flex-1" style={{ background: "var(--line)" }} />
            </div>
            <button type="button" data-testid="google-login-btn" onClick={googleLogin}
              className="w-full flex items-center justify-center gap-2.5 py-2.5 rounded border text-sm font-semibold transition-colors hover:bg-white/5"
              style={{ borderColor: "var(--line)", color: "var(--text)" }}>
              <GoogleIcon /> Continue with Google
            </button>
          </>
        )}
        {devLink && (
          <div className="mt-4 p-3 rounded text-xs" style={{ border: "1px dashed var(--gold)", color: "var(--muted)" }} data-testid="dev-reset-link-box">
            <b style={{ color: "var(--gold)" }}>Demo mode</b> (no email key configured yet) — use this link to reset:<br />
            <Link to={devLink.replace(window.location.origin, "")} style={{ color: "var(--bp)", wordBreak: "break-all" }} data-testid="dev-reset-link">{devLink}</Link>
          </div>
        )}
        {mode === "login" && (
          <button data-testid="auth-forgot-link" onClick={() => { setMode("forgot"); setDevLink(null); }}
            className="block mx-auto mt-4 text-sm hover:underline" style={{ color: "var(--muted)" }}>Forgot password?</button>
        )}
        {mode !== "reset" && (
          <button data-testid="auth-toggle-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setDevLink(null); }}
            className="block mx-auto mt-3 text-sm hover:underline" style={{ color: "var(--bp)" }}>
            {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
          </button>
        )}
      </div>
    </div>
  );
}
